/**
 * search-overlay.js
 * ---------------------------------------------------------------------------
 * Behaviour for the full-screen header search overlay.
 *
 * This file deliberately does NOT re-implement anything theme.js already does.
 * theme.js owns the <predictive-search> custom element: it adds `.is-active`,
 * adds `predictive-overflow-hidden` to <body>, fetches results into
 * `#predictive-search.search__results` and toggles that node's inline
 * `style.display`. We only listen to the events it already dispatches
 * (`predictive-search:open` / `:close` / `:close-all`) and layer on:
 *
 *   1. showing/hiding the empty-state panel `[data-search-default]`
 *   2. a body class + scroll lock that survives the CSS `position: fixed` trick
 *   3. recent searches   (localStorage `vacier:recent-searches`)
 *   4. recently viewed   (localStorage `theme-recent` + product JSON fetches)
 *
 * Everything degrades silently: no overlay markup, no storage (Safari private
 * mode throws on write), a 404 on a product JSON — none of it should throw.
 */
(function () {
  'use strict';

  // Keep storefront JSON requests on the active locale's path prefix.
  function searchOverlayRoot() {
    var root = (window.theme && window.theme.routes && window.theme.routes.home) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  /* ---------------------------------------------------------------------- */
  /* Config                                                                  */
  /* ---------------------------------------------------------------------- */

  var RECENT_KEY = 'vacier:recent-searches';
  var PRODUCT_CACHE_KEY = 'vacier:search-product-cache';
  var THEME_RECENT_KEY = 'theme-recent';

  var RECENT_LIMIT = 5;
  var VIEWED_LIMIT = 8;
  var INPUT_DEBOUNCE = 150;
  var IMAGE_SIZE = '540x';
  var IMAGE_WIDTH = 540;

  var ROOT_SELECTOR = 'predictive-search[data-context="header"]';
  // Panel state for "query ran, found nothing": chips stay, history hides.
  var SUGGESTIONS_ONLY = 'search-default--suggestions-only';

  /* ---------------------------------------------------------------------- */
  /* Storage — every access is wrapped, with an in-memory fallback so that a  */
  /* throwing localStorage (private mode) just means "state lasts one page".  */
  /* ---------------------------------------------------------------------- */

  var memory = {};

  function storageGet(store, key) {
    try {
      var raw = window[store].getItem(key);
      if (raw === null || raw === undefined) return null;
      return JSON.parse(raw);
    } catch (e) {
      return Object.prototype.hasOwnProperty.call(memory, key)
        ? memory[key]
        : null;
    }
  }

  function storageSet(store, key, value) {
    memory[key] = value;
    try {
      window[store].setItem(key, JSON.stringify(value));
    } catch (e) {
      /* quota, private mode, disabled storage — memory copy is enough */
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Small helpers                                                           */
  /* ---------------------------------------------------------------------- */

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  /**
   * Rewrite a Shopify CDN image URL to request a specific size, e.g.
   *   .../foo.jpg?v=1  ->  .../foo_540x.jpg?v=1
   * Any size suffix already present is replaced. Anything that does not look
   * like an image URL is handed back untouched.
   */
  function sizedImageUrl(url, size) {
    if (!url) return '';
    url = String(url);

    // theme.js stores the recently-viewed thumbnail as the product page's
    // data-img-url, which Liquid emits with a literal "{width}" token still in
    // it (.../foo_{width}x.jpg) for the srcset builder to fill in later. Left
    // alone it 404s, and appending our own size on top of it produces
    // "_{width}x_540x.jpg", which 404s just as hard -- so the token is filled
    // in rather than added to.
    if (url.indexOf('{width}') !== -1) {
      return url.replace(/\{width\}/g, String(parseInt(size, 10) || IMAGE_WIDTH));
    }

    var match = url.match(
      /^(.*?)(?:_(?:\d+x\d*|x\d+|\d+x))?(\.(?:jpe?g|png|gif|webp))(\?.*)?$/i
    );
    if (!match) return url;
    return match[1] + '_' + size + match[2] + (match[3] || '');
  }

  function searchUrlFor(term) {
    return (
      '/search?q=' +
      encodeURIComponent(term) +
      '&type=product&options%5Bprefix%5D=last'
    );
  }

  /**
   * theme.Currency.formatMoney may emit markup (superscript decimals), which is
   * why its output is injected as HTML rather than escaped — same as the rest
   * of the theme does. The fallback is a plain number, so it is inert.
   */
  function formatPrice(cents) {
    var t = window.theme;
    try {
      if (t && t.Currency && typeof t.Currency.formatMoney === 'function') {
        var fmt = t.moneyFormat || (t.settings && t.settings.moneyFormat);
        return t.Currency.formatMoney(cents, fmt);
      }
    } catch (e) {
      /* fall through */
    }
    var amount = Number(cents);
    if (isNaN(amount)) return '';
    return (amount / 100).toFixed(2);
  }

  /* ---------------------------------------------------------------------- */
  /* Recent searches                                                         */
  /* ---------------------------------------------------------------------- */

  function readRecent() {
    var stored = storageGet('localStorage', RECENT_KEY);
    if (!stored || Object.prototype.toString.call(stored) !== '[object Array]') {
      return [];
    }
    var out = [];
    for (var i = 0; i < stored.length; i++) {
      if (typeof stored[i] === 'string' && stored[i].trim()) {
        out.push(stored[i]);
      }
    }
    return out.slice(0, RECENT_LIMIT);
  }

  function writeRecent(terms) {
    storageSet('localStorage', RECENT_KEY, terms.slice(0, RECENT_LIMIT));
  }

  function addRecent(term) {
    term = String(term == null ? '' : term).trim();
    if (!term) return;

    var terms = readRecent();
    var lower = term.toLowerCase();
    var deduped = [term];

    for (var i = 0; i < terms.length; i++) {
      if (terms[i].toLowerCase() !== lower) deduped.push(terms[i]);
    }

    writeRecent(deduped);
  }

  /* ---------------------------------------------------------------------- */
  /* Recently viewed — product lookups                                       */
  /* ---------------------------------------------------------------------- */

  /**
   * `theme-recent` is written by theme.js as an insertion-ordered object,
   * oldest first. We want the newest handles, so reverse before slicing.
   */
  function readThemeRecent() {
    var store = storageGet('localStorage', THEME_RECENT_KEY);
    if (!store || typeof store !== 'object') return [];

    var handles = Object.keys(store).reverse();
    var out = [];
    for (var i = 0; i < handles.length && out.length < VIEWED_LIMIT; i++) {
      var handle = handles[i];
      if (!handle) continue;
      out.push({ handle: handle, meta: store[handle] || {} });
    }
    return out;
  }

  function readProductCache() {
    var cache = storageGet('sessionStorage', PRODUCT_CACHE_KEY);
    return cache && typeof cache === 'object' ? cache : {};
  }

  function writeProductCache(cache) {
    storageSet('sessionStorage', PRODUCT_CACHE_KEY, cache);
  }

  /**
   * Resolves to a trimmed product record, or null when the handle is gone
   * (404) / the network failed. Callers drop nulls.
   */
  function fetchProduct(handle, cache) {
    if (cache[handle]) return Promise.resolve(cache[handle]);

    if (typeof window.fetch !== 'function') return Promise.resolve(null);

    return window
      .fetch(searchOverlayRoot() + 'products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin'
      })
      .then(function (response) {
        if (!response.ok) return null;
        return response.json();
      })
      .then(function (product) {
        if (!product || !product.title) return null;
        var record = {
          handle: handle,
          title: product.title,
          url: product.url || '/products/' + handle,
          price: product.price != null ? product.price : product.price_min,
          featured_image: product.featured_image || ''
        };
        cache[handle] = record;
        return record;
      })
      .catch(function () {
        return null;
      });
  }

  /* ---------------------------------------------------------------------- */
  /* The overlay controller                                                  */
  /* ---------------------------------------------------------------------- */

  var refs = null; // current DOM references, refreshed by init()
  var isOpen = false;
  var savedScrollY = 0;
  var viewedLoaded = false;

  function collectRefs() {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) return null;

    return {
      root: root,
      form: root.querySelector('.search__form'),
      input: root.querySelector('input[type="search"]'),
      panel: root.querySelector('[data-search-default]'),
      results: root.querySelector('#predictive-search'),
      recentSection: root.querySelector('[data-recent]'),
      recentList: root.querySelector('[data-recent-list]'),
      viewedSection: root.querySelector('[data-viewed]'),
      viewedList: root.querySelector('[data-viewed-list]')
    };
  }

  /* --- default panel ----------------------------------------------------- */

  function showPanel() {
    if (refs && refs.panel) refs.panel.removeAttribute('hidden');
  }

  function hidePanel() {
    if (refs && refs.panel) refs.panel.setAttribute('hidden', 'hidden');
  }

  /**
   * Empty the predictive results.
   *
   * theme.js owns that container, but its onChange() bails on an empty query
   * ("if (!searchTerm.length) return") WITHOUT clearing what is already in
   * there, and the only code path that does clear it is close(), which tears
   * the whole overlay down. So deleting the query left the last set of products
   * and the "View more" button on screen underneath the empty state. This is
   * close()'s clean-up, minus the closing.
   */
  function clearResults() {
    if (!refs || !refs.results) return;
    if (refs.results.innerHTML === '') return; // also stops the observer looping
    refs.results.innerHTML = '';
    refs.results.style.display = 'none';
  }

  function inputIsEmpty() {
    return !String(refs && refs.input ? refs.input.value : '').trim();
  }

  /**
   * theme.js drives `#predictive-search` through an inline style.display, which
   * is awkward to observe, so the panel mirrors the INPUT instead: empty query
   * means "no results are possible", which is exactly when the empty state
   * should be on screen -- and nothing else should be.
   */
  function syncPanel() {
    if (!refs || !refs.panel) return;
    if (inputIsEmpty()) {
      clearResults();
      refs.panel.classList.remove(SUGGESTIONS_ONLY);
      showPanel();
    } else {
      syncNoResults();
    }
  }

  /**
   * The no-results state.
   *
   * snippets/search-results.liquid marks an empty result set with
   * .predictive-search-results--none. When that comes back we keep the panel on
   * screen so POPULAR SEARCHES sits under the "try one of these instead"
   * sentence, but put it in suggestions-only mode: recent searches and recently
   * viewed are the shopper's own history, which is not an answer to a query
   * that found nothing.
   */
  function syncNoResults() {
    if (!refs || !refs.panel) return;

    var none =
      refs.results && refs.results.querySelector('.predictive-search-results--none');

    if (none) {
      refs.panel.classList.add(SUGGESTIONS_ONLY);
      showPanel();
    } else {
      refs.panel.classList.remove(SUGGESTIONS_ONLY);
      hidePanel();
    }
  }

  /* --- scroll lock ------------------------------------------------------- */

  /**
   * The CSS agent locks mobile scrolling with `position: fixed` on <body>,
   * which collapses the scroll position to 0. We stash the offset, pin the body
   * at a matching negative `top` so nothing visibly jumps, then restore it on
   * close. Setting `top` is harmless when the body is not fixed.
   */
  function lockScroll() {
    savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.classList.add('search-overlay-open');
    document.body.style.top = '-' + savedScrollY + 'px';
  }

  function unlockScroll() {
    document.body.classList.remove('search-overlay-open');
    document.body.style.top = '';
    window.scrollTo(0, savedScrollY);
  }

  /* --- open / close ------------------------------------------------------ */

  function onOpen(event) {
    if (!event || !event.detail || event.detail.context !== 'header') return;
    if (!refs) refs = collectRefs();
    if (!refs) return;

    if (!isOpen) {
      isOpen = true;
      lockScroll();
    }

    syncPanel();
    renderRecent();
    loadViewed();
  }

  function onClose() {
    if (isOpen) {
      isOpen = false;
      unlockScroll();
    }
    // Reset the query as well as the results. theme.js empties the results
    // container on close but leaves whatever was typed in the field, so
    // reopening showed a stale term with nothing under it -- the one state the
    // empty-state panel is meant to replace. Only the header instance is
    // touched; /search keeps the term in its own bar.
    if (refs && refs.input) refs.input.value = '';
    clearResults();
    if (refs && refs.panel) refs.panel.classList.remove(SUGGESTIONS_ONLY);
    showPanel();
  }

  /* --- recent searches rendering ---------------------------------------- */

  function renderRecent() {
    if (!refs || !refs.recentList) return;

    var terms = readRecent();
    var html = '';

    for (var i = 0; i < terms.length; i++) {
      var term = terms[i];
      html +=
        '<li class="search-recent__item">' +
        '<a class="search-recent__term" href="' +
        escapeHtml(searchUrlFor(term)) +
        '">' +
        escapeHtml(term) +
        '</a>' +
        '<button type="button" class="search-recent__remove" data-recent-remove aria-label="' + ((window.theme && theme.strings && theme.strings.remove) || "Remove") + '">' +
        '<svg class="icon" viewBox="0 0 64 64" aria-hidden="true" focusable="false">' +
        '<path d="M19 17.61l27.12 27.13m0-27.13L19 44.74"/>' +
        '</svg>' +
        '</button>' +
        '</li>';
    }

    refs.recentList.innerHTML = html;

    if (refs.recentSection) {
      if (terms.length) {
        refs.recentSection.removeAttribute('hidden');
      } else {
        refs.recentSection.setAttribute('hidden', 'hidden');
      }
    }
  }

  function removeRecentAt(index) {
    var terms = readRecent();
    if (index < 0 || index >= terms.length) return;
    terms.splice(index, 1);
    writeRecent(terms);
    renderRecent();
  }

  function clearRecent() {
    writeRecent([]);
    renderRecent();
  }

  /* --- recently viewed rendering ---------------------------------------- */

  function renderViewed(entries, products) {
    if (!refs || !refs.viewedList) return;

    var html = '';
    var rendered = 0;

    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      if (!product) continue;

      var meta = entries[i] ? entries[i].meta || {} : {};
      var url = meta.url || product.url || '/products/' + product.handle;

      // Prefer the image theme.js already cached for this handle: it is almost
      // certainly in the browser cache already, so the card paints without a
      // flash while the JSON-sourced URL would be a fresh request.
      var rawImage = meta.featuredImage || product.featured_image || '';
      if (!rawImage) continue;

      var src = sizedImageUrl(rawImage, IMAGE_SIZE);
      var ratio = parseFloat(meta.aspectRatio);
      var height =
        ratio && ratio > 0 ? Math.round(IMAGE_WIDTH / ratio) : IMAGE_WIDTH;

      html +=
        '<a class="search-viewed__card" href="' +
        escapeHtml(url) +
        '">' +
        '<div class="search-viewed__image-wrap">' +
        '<img class="search-viewed__image" src="' +
        escapeHtml(src) +
        '" alt="' +
        escapeHtml(product.title) +
        '" loading="lazy" width="' +
        IMAGE_WIDTH +
        '" height="' +
        height +
        '">' +
        '</div>' +
        '<div class="search-viewed__title">' +
        escapeHtml(product.title) +
        '</div>' +
        '<div class="search-viewed__price">' +
        formatPrice(product.price) +
        '</div>' +
        '</a>';

      rendered++;
    }

    refs.viewedList.innerHTML = html;

    if (refs.viewedSection) {
      if (rendered) {
        refs.viewedSection.removeAttribute('hidden');
      } else {
        refs.viewedSection.setAttribute('hidden', 'hidden');
      }
    }
  }

  /**
   * Deliberately lazy: nothing here runs until the shopper actually opens the
   * overlay, so a page load never pays for these requests.
   */
  function loadViewed() {
    if (viewedLoaded) return;
    if (!refs || !refs.viewedList) return;
    viewedLoaded = true;

    var entries = readThemeRecent();
    if (!entries.length) return;

    var cache = readProductCache();
    var requests = [];

    for (var i = 0; i < entries.length; i++) {
      requests.push(fetchProduct(entries[i].handle, cache));
    }

    Promise.all(requests)
      .then(function (products) {
        writeProductCache(cache);
        renderViewed(entries, products);
      })
      .catch(function () {
        /* a single rejection should never blank the overlay */
      });
  }

  /* ---------------------------------------------------------------------- */
  /* Wiring                                                                  */
  /* ---------------------------------------------------------------------- */

  var syncPanelDebounced = debounce(syncPanel, INPUT_DEBOUNCE);

  function bindInstance(current) {
    if (current.root.getAttribute('data-search-overlay-bound') === 'true') {
      return;
    }
    current.root.setAttribute('data-search-overlay-bound', 'true');

    if (current.input) {
      current.input.addEventListener('input', syncPanelDebounced);
      // Fired by the native "x" affordance on type="search" inputs, which does
      // not emit a useful `input` event in every browser.
      current.input.addEventListener('search', syncPanel);
    }

    // theme.js debounces 300ms and then fetches, so the response to the LAST
    // keystroke can land well after the shopper has finished deleting the
    // query -- repopulating the container we just emptied and putting the
    // products back underneath the empty state. Watching the container catches
    // that late write whoever made it; clearResults() no-ops when it is already
    // empty, so this cannot loop on its own mutation.
    if (current.results && typeof window.MutationObserver === 'function') {
      new MutationObserver(function () {
        if (inputIsEmpty()) {
          clearResults();
          return;
        }
        // theme.js has just swapped the results in; decide whether they are a
        // real result set or the empty state.
        syncNoResults();
      }).observe(current.results, { childList: true, subtree: true });
    }

    if (current.form) {
      current.form.addEventListener('submit', function () {
        if (current.input) addRecent(current.input.value);
      });
    }

    // Delegated off the list container so re-renders never drop the binding.
    if (current.recentList) {
      current.recentList.addEventListener('click', function (event) {
        var button = event.target && event.target.closest
          ? event.target.closest('[data-recent-remove]')
          : null;
        if (!button) return;

        // These buttons live inside the search form; without this they submit.
        event.preventDefault();

        var item = button.closest('.search-recent__item');
        if (!item || !item.parentNode) return;

        var index = Array.prototype.indexOf.call(
          item.parentNode.children,
          item
        );
        removeRecentAt(index);
      });
    }

    if (current.recentSection) {
      current.recentSection.addEventListener('click', function (event) {
        var button = event.target && event.target.closest
          ? event.target.closest('[data-recent-clear]')
          : null;
        if (!button) return;
        event.preventDefault();
        clearRecent();
      });
    }
  }

  function init() {
    refs = collectRefs();
    if (!refs) return;

    bindInstance(refs);
    renderRecent();
    syncPanel();
  }

  document.addEventListener('predictive-search:open', onOpen);
  document.addEventListener('predictive-search:close', onClose);
  document.addEventListener('predictive-search:close-all', onClose);

  // Theme editor: the header section is re-rendered wholesale, so the old
  // element (and its listeners) is thrown away and we re-resolve everything.
  document.addEventListener('shopify:section:load', function (event) {
    var target = event && event.target;
    if (!target || !target.querySelector) return;
    if (!target.querySelector(ROOT_SELECTOR)) return;

    isOpen = false;
    viewedLoaded = false;
    document.body.classList.remove('search-overlay-open');
    document.body.style.top = '';
    init();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
